import { useEffect, useState } from 'react';
import { getMelhoria } from '../lib/api';

interface Issue {
  id: number;
  assinatura: string;
  fonte: string;
  titulo: string | null;
  tipo: string;
  correcao: string;
  n_casos: number;
  n_orgs: number;
  delta_mediano: number | null;
  casos_semente: Array<{ caso: number; nosso: string; perito: string }> | null;
  verificacao: any;
  status: string;
  created_at: string | null;
}
interface Suspeito {
  processo_id: number;
  org_id: number | null;
  score: number;
  flags: Record<string, boolean> | null;
  status: string;
  dia: string;
}
interface Fix {
  id: number;
  issue_id: number | null;
  assinatura: string;
  titulo: string | null;
  causa_raiz: { five_whys?: string[]; hipotese_raiz?: string; pode_regredir?: string } | null;
  arquivos_suspeitos: Array<{ arquivo: string; funcao: string; motivo: string }> | null;
  fix_abordagem: string | null;
  flag_proposto: string | null;
  classe: string | null;
  risco: string | null;
  golden_seeds: number[] | null;
  status: string;
  golden5: any;
  dry_run: boolean;
  updated_at: string | null;
}

export default function MelhoriaPage() {
  const [issues, setIssues] = useState<Issue[]>([]);
  const [suspeitos, setSuspeitos] = useState<Suspeito[]>([]);
  const [fixes, setFixes] = useState<Fix[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    getMelhoria()
      .then((r) => {
        setIssues(r.data.issues ?? []);
        setSuspeitos(r.data.suspeitos ?? []);
        setFixes(r.data.fixes ?? []);
      })
      .catch((e) => setErr(e?.message ?? 'erro'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="p-6 text-slate-400">Carregando…</div>;
  if (err) return <div className="p-6 text-rose-400">Erro: {err}</div>;

  return (
    <div className="space-y-8 p-6">
      <div>
        <h1 className="text-xl font-semibold text-slate-100">Melhoria Contínua</h1>
        <p className="mt-1 text-sm text-slate-400">
          Saída das máquinas: <b>propostas de fix</b> (Loop 2: issue → causa-raiz → fix gated),
          <b> issues</b> (edições do perito clusterizadas) e <b>casos suspeitos</b> (red-flags
          de comportamento → fonte “ii” do P bayesiano).
        </p>
      </div>

      {/* Loop 2 — propostas de fix do sistema (causa-raiz + classe A/B) */}
      <section>
        <h2 className="mb-2 text-sm font-medium text-slate-300">
          🛠️ Propostas de fix do sistema (Loop 2) — {fixes.length}
        </h2>
        <p className="mb-3 text-xs text-slate-500">
          Cada issue verificada vira uma proposta com causa-raiz (5-whys) e um fix MÍNIMO e gated.
          <b className="text-emerald-300"> Classe A</b> = mecânico/gated, pronto p/ golden5 + ligar.
          <b className="text-amber-300"> Classe B</b> = mexe em lógica → exige sua aprovação.
          Nada é aplicado automaticamente — o cron só rascunha.
        </p>
        <div className="space-y-3">
          {fixes.map((f) => (
            <div key={f.id} className="rounded-xl border border-slate-800 bg-slate-900/40 p-4">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="text-slate-100">{f.titulo ?? f.assinatura}</div>
                  <div className="text-xs text-slate-500">{f.assinatura}</div>
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  <span
                    className={`rounded px-2 py-0.5 text-xs font-semibold ${
                      f.classe === 'A'
                        ? 'bg-emerald-600/20 text-emerald-300'
                        : 'bg-amber-600/20 text-amber-300'
                    }`}
                  >
                    Classe {f.classe}
                  </span>
                  <span className="rounded bg-slate-700/40 px-2 py-0.5 text-xs text-slate-300">
                    risco {f.risco}
                  </span>
                  <span className="rounded bg-slate-700/40 px-2 py-0.5 text-xs text-slate-400">
                    {f.status}
                  </span>
                </div>
              </div>
              {f.causa_raiz?.hipotese_raiz && (
                <div className="mt-2 text-sm text-slate-300">
                  <b className="text-slate-400">Causa-raiz:</b> {f.causa_raiz.hipotese_raiz}
                </div>
              )}
              {f.causa_raiz?.five_whys && f.causa_raiz.five_whys.length > 0 && (
                <ol className="mt-1 list-decimal pl-5 text-xs text-slate-500">
                  {f.causa_raiz.five_whys.map((w, i) => (
                    <li key={i}>{w}</li>
                  ))}
                </ol>
              )}
              {f.fix_abordagem && (
                <div className="mt-2 text-sm text-slate-300">
                  <b className="text-slate-400">Fix proposto:</b> {f.fix_abordagem}
                </div>
              )}
              <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-500">
                {f.flag_proposto && (
                  <span>
                    flag: <code className="text-slate-300">{f.flag_proposto}</code>
                  </span>
                )}
                {f.arquivos_suspeitos && f.arquivos_suspeitos.length > 0 && (
                  <span>
                    arquivos:{' '}
                    {f.arquivos_suspeitos.map((a) => a.arquivo).join(', ')}
                  </span>
                )}
                {f.golden_seeds && f.golden_seeds.length > 0 && (
                  <span>golden-seeds: {f.golden_seeds.join(', ')}</span>
                )}
              </div>
              {f.causa_raiz?.pode_regredir && (
                <div className="mt-1 text-xs text-rose-300/80">
                  ⚠️ pode regredir: {f.causa_raiz.pode_regredir}
                </div>
              )}
            </div>
          ))}
          {fixes.length === 0 && (
            <div className="rounded-xl border border-slate-800 px-3 py-4 text-center text-slate-500">
              Nenhuma proposta ainda.
            </div>
          )}
        </div>
      </section>

      {/* Issues de melhoria (das edições) */}
      <section>
        <h2 className="mb-2 text-sm font-medium text-slate-300">
          🔧 Issues de melhoria — {issues.length}
        </h2>
        <div className="overflow-x-auto rounded-xl border border-slate-800">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-900/60 text-xs uppercase text-slate-400">
              <tr>
                <th className="px-3 py-2 text-left">Issue</th>
                <th className="px-3 py-2 text-center">Casos</th>
                <th className="px-3 py-2 text-center">Orgs</th>
                <th className="px-3 py-2 text-left">Casos-semente (nosso → perito)</th>
                <th className="px-3 py-2 text-center">Status</th>
              </tr>
            </thead>
            <tbody>
              {issues.map((it) => (
                <tr key={it.id} className="border-t border-slate-800">
                  <td className="px-3 py-2">
                    <div className="text-slate-200">{it.titulo ?? it.assinatura}</div>
                    <div className="text-xs text-slate-500">{it.assinatura}</div>
                  </td>
                  <td className="px-3 py-2 text-center font-semibold text-slate-200">{it.n_casos}</td>
                  <td className="px-3 py-2 text-center text-slate-300">{it.n_orgs}</td>
                  <td className="px-3 py-2 text-xs text-slate-400">
                    {(it.casos_semente ?? []).slice(0, 3).map((s, i) => (
                      <span key={i} className="mr-2 whitespace-nowrap">
                        #{s.caso}: {String(s.nosso || '∅').slice(0, 10)}→{String(s.perito).slice(0, 10)}
                      </span>
                    ))}
                  </td>
                  <td className="px-3 py-2 text-center">
                    <span className="rounded bg-amber-600/20 px-2 py-0.5 text-xs text-amber-300">
                      {it.status}
                    </span>
                  </td>
                </tr>
              ))}
              {issues.length === 0 && (
                <tr><td colSpan={5} className="px-3 py-4 text-center text-slate-500">Nenhuma issue ainda.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      {/* Casos suspeitos (comportamento) */}
      <section>
        <h2 className="mb-2 text-sm font-medium text-slate-300">
          🕵️ Casos suspeitos por comportamento (últimos 2 dias) — {suspeitos.length}
        </h2>
        <div className="overflow-x-auto rounded-xl border border-slate-800">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-900/60 text-xs uppercase text-slate-400">
              <tr>
                <th className="px-3 py-2 text-left">Caso</th>
                <th className="px-3 py-2 text-center">Score</th>
                <th className="px-3 py-2 text-left">Red-flags</th>
                <th className="px-3 py-2 text-center">Org</th>
                <th className="px-3 py-2 text-center">Dia</th>
              </tr>
            </thead>
            <tbody>
              {suspeitos.map((s) => (
                <tr key={`${s.processo_id}-${s.dia}`} className="border-t border-slate-800">
                  <td className="px-3 py-2">
                    <a href={`/cases/${s.processo_id}`} className="text-blue-300 hover:underline">
                      #{s.processo_id}
                    </a>
                  </td>
                  <td className="px-3 py-2 text-center font-semibold text-rose-300">{s.score}</td>
                  <td className="px-3 py-2 text-xs text-slate-300">
                    {Object.keys(s.flags ?? {}).join(', ')}
                  </td>
                  <td className="px-3 py-2 text-center text-slate-400">{s.org_id ?? '—'}</td>
                  <td className="px-3 py-2 text-center text-xs text-slate-500">{s.dia}</td>
                </tr>
              ))}
              {suspeitos.length === 0 && (
                <tr><td colSpan={5} className="px-3 py-4 text-center text-slate-500">Nenhum suspeito.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
